import {
  PlayerSessionUnauthorizedError,
  playerApi,
  playerEventsURL,
  type PlayerSocialState,
} from '../api/public.ts'
import {
  createPlayerActionID,
  isPlayerCommunicationPolicyChanged,
  parsePlayerChatChangedHint,
  parsePlayerSocialEvent,
  type PlayerChatChangedHint,
} from './playerSocialPresentation.ts'
import type { PlayerSocialDockSession } from './playerSocialDockRuntime.ts'
import { onScopeDispose, ref } from 'vue'

type PlayerSocialEvent = NonNullable<
  ReturnType<typeof parsePlayerSocialEvent>
>

type PlayerPresenceDockDependencies = {
  session: PlayerSocialDockSession
  onStreamOpen: (connectionCycle: number) => void
  onStreamInterrupted: () => void
  onSocialSnapshot: (state: PlayerSocialState) => void
  onSocialChanged: (observedAt: number) => void
  onActivityChanged: () => void
  onPresenceChanged: (event: PlayerSocialEvent) => void
  onPartyChanged: (observedAt: number) => void
  onChatChanged: (
    hint: PlayerChatChangedHint | null,
    observedAt: number,
  ) => void
  onCommunicationPolicyChanged: (observedAt: number) => void
  onPageHide: () => void
  onPageShow: () => void
  onHidden: () => void
  onVisible: () => void
}

export function usePlayerSocialDockPresence(
  dependencies: PlayerPresenceDockDependencies,
) {
  const { session } = dependencies
  const { options } = session
  const streamConnected = ref(false)

  let heartbeatRequestID = 0
  let eventSource: EventSource | null = null
  let heartbeatTimer: number | undefined
  let presenceClientID = createPlayerActionID('page')

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', handlePageHide)
    window.addEventListener('pageshow', handlePageShow)
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  onScopeDispose(() => {
    if (typeof window === 'undefined') return
    window.removeEventListener('pagehide', handlePageHide)
    window.removeEventListener('pageshow', handlePageShow)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })

  function start(): void {
    openEventSource()
    void renewPresence()
  }

  function stop(releasePresence: boolean): void {
    closeEventSource()
    clearHeartbeatTimer()
    heartbeatRequestID += 1
    if (releasePresence) {
      const releasedClientID = rotatePresenceClientID()
      void playerApi.releasePresence(releasedClientID, true)
        .catch(() => undefined)
    }
  }

  function reset(): void {
    streamConnected.value = false
  }

  function openEventSource(): void {
    if (
      eventSource ||
      !options.authenticated() ||
      (typeof document !== 'undefined' && document.hidden) ||
      typeof EventSource === 'undefined'
    ) {
      return
    }
    const source = new EventSource(playerEventsURL)
    eventSource = source
    let connectionCycle = 0
    let reconnectPending = false
    source.onopen = () => {
      if (eventSource !== source) return
      reconnectPending = false
      streamConnected.value = true
      dependencies.onStreamOpen(connectionCycle)
    }
    source.onerror = () => {
      if (eventSource !== source) return
      streamConnected.value = false
      if (!reconnectPending) {
        connectionCycle += 1
        reconnectPending = true
        dependencies.onStreamInterrupted()
      }
    }
    source.onmessage = event => {
      if (eventSource !== source) return
      const envelope = parsePlayerSocialEvent(event.data)
      if (!envelope) return
      const observedAt = Date.now()
      switch (envelope.event) {
        case 'social.snapshot': {
          const data = envelope.data as { state?: PlayerSocialState }
          if (data.state) dependencies.onSocialSnapshot(data.state)
          break
        }
        case 'social.changed':
          dependencies.onSocialChanged(observedAt)
          break
        case 'activity.changed':
          dependencies.onActivityChanged()
          break
        case 'presence.changed':
          dependencies.onPresenceChanged(envelope)
          break
        case 'party.changed':
          dependencies.onPartyChanged(observedAt)
          break
        case 'chat.changed':
          dependencies.onChatChanged(
            parsePlayerChatChangedHint(envelope),
            observedAt,
          )
          break
        case 'communication.policy_changed':
          if (isPlayerCommunicationPolicyChanged(envelope)) {
            dependencies.onCommunicationPolicyChanged(observedAt)
          }
          break
      }
    }
  }

  function closeEventSource(): void {
    eventSource?.close()
    eventSource = null
    streamConnected.value = false
  }

  function reconnectEventSource(): void {
    closeEventSource()
    openEventSource()
  }

  async function renewPresence(): Promise<void> {
    if (!options.authenticated()) return
    clearHeartbeatTimer()
    const generation = session.generation()
    const actorSteamID = options.steamID()
    const clientID = presenceClientID
    const requestID = ++heartbeatRequestID
    try {
      const heartbeat = await playerApi.heartbeatPresence(clientID)
      if (
        !session.isCurrentActorRequest(
          generation,
          actorSteamID,
          requestID,
          heartbeatRequestID,
        ) ||
        clientID !== presenceClientID
      ) {
        return
      }
      scheduleHeartbeat(Math.max(1, heartbeat.heartbeat_interval_s))
    } catch (error) {
      if (
        !session.isCurrentActorRequest(
          generation,
          actorSteamID,
          requestID,
          heartbeatRequestID,
        ) ||
        clientID !== presenceClientID
      ) {
        return
      }
      if (error instanceof PlayerSessionUnauthorizedError) {
        options.onUnauthorized()
        return
      }
      scheduleHeartbeat(15)
    }
  }

  function scheduleHeartbeat(intervalSeconds: number): void {
    if (!options.authenticated() || typeof window === 'undefined') return
    heartbeatTimer = window.setTimeout(
      () => void renewPresence(),
      intervalSeconds * 1000,
    )
  }

  function clearHeartbeatTimer(): void {
    if (heartbeatTimer === undefined || typeof window === 'undefined') return
    window.clearTimeout(heartbeatTimer)
    heartbeatTimer = undefined
  }

  function handlePageHide(): void {
    stop(true)
    dependencies.onPageHide()
  }

  function handlePageShow(): void {
    if (!options.authenticated()) return
    start()
    dependencies.onPageShow()
  }

  function handleVisibilityChange(): void {
    if (!options.authenticated()) return
    if (document.hidden) {
      // A hidden tab must not keep an SSE slot. Heartbeats remain active until
      // pagehide/session stop, matching the presence lease contract.
      closeEventSource()
      dependencies.onHidden()
      return
    }
    openEventSource()
    dependencies.onVisible()
  }

  function rotatePresenceClientID(): string {
    const previousClientID = presenceClientID
    presenceClientID = createPlayerActionID('page')
    return previousClientID
  }

  return {
    streamConnected,
    start,
    stop,
    reset,
    reconnectEventSource,
  }
}
